import { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * Metadata list: label left, value right; optional row icons; uniform dividers.
 *
 * @param {Array<{
 *   label: string,
 *   value: string|number|import('react').ReactNode,
 *   icon?: import('lucide-react').LucideIcon,
 *   valueClassName?: string,
 *   className?: string,
 * }>} props.items
 * @param {boolean} [props.bordered=true]  Horizontal rules between rows.
 * @param {string} [props.className]
 */
const DrawerKeyValueList = ({ items = [], bordered = true, className = '' }) => {
  const list = Array.isArray(items) ? items : [];

  return (
    <div
      className={[
        bordered ? 'divide-y divide-edge' : 'flex flex-col gap-1',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {list.map((item, idx) => {
        const Icon = item.icon;
        const rowClass = [
          'flex items-start justify-between gap-4',
          bordered ? 'py-3' : 'py-0.5',
          item.className,
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <div key={item.key ?? `kv-${idx}`} className={rowClass}>
            <div className="flex min-w-0 items-center gap-2">
              {Icon ? <Icon className="size-3.5 shrink-0 text-content-muted" aria-hidden /> : null}
              <span className="text-sm text-content-muted">{item.label}</span>
            </div>
            <div
              className={[
                'min-w-0 shrink text-right text-sm text-content',
                item.valueClassName,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {item.value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

DrawerKeyValueList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired,
      value: PropTypes.node.isRequired,
      icon: PropTypes.elementType,
      valueClassName: PropTypes.string,
      className: PropTypes.string,
    }),
  ),
  bordered: PropTypes.bool,
  className: PropTypes.string,
};

export default memo(DrawerKeyValueList);
